import { createContext, useContext } from 'react';

import CreditCardImg from 'assets/brand/credit-card.png';
import RegisterImg from 'assets/brand/register.png';
import useActivationOnboardingProgress from 'common/hooks/useActivationOnboardingProgress/useActivationOnboardingProgress';
import { FCWithChildren } from 'common/types';
import * as bankRoutes from 'features/Bank/routes';
import * as paymentCardRoutes from 'features/PaymentCards/routes';

import locales from './pages/Setup/locales';

export interface ActionItem {
  image: string;
  isCompleted: boolean | undefined;
  isSkipped: boolean | undefined;
  isMandatory: boolean;
  key: 'FIRST_PAYIN' | 'FIRST_CARD_ORDERED';
  text: string;
  to: string;
}
interface PostOnboardingAccountSetupContextType {
  actionItems: ActionItem[];
  hasCompletedPostOnboardingSetup: boolean | undefined;
  allActionsCompleted: boolean;
  progressLabel: string;
  progressValue: number;
  loading: boolean;
}

const PostOnboardingAccountSetupContext =
  createContext<PostOnboardingAccountSetupContextType>({
    actionItems: [],
    allActionsCompleted: false,
    hasCompletedPostOnboardingSetup: false,
    loading: false,
    progressLabel: '',
    progressValue: 0,
  });

export const PostOnboardingAccountSetupContextProvider: FCWithChildren = ({
  children,
}) => {
  const { completed, done, loading, skipped } =
    useActivationOnboardingProgress();

  const ACTION_ITEMS: ActionItem[] = [
    {
      image: RegisterImg,
      isCompleted: done?.some((i) => i === 'FIRST_PAYIN'),
      isMandatory: true,
      isSkipped: skipped?.some((i) => i === 'FIRST_PAYIN'),
      key: 'FIRST_PAYIN',
      text: locales.addBalance,
      to: bankRoutes.root,
    },
    {
      image: CreditCardImg,
      isCompleted: done?.some((i) => i === 'FIRST_CARD_ORDERED'),
      isMandatory: false,
      isSkipped: skipped?.some((i) => i === 'FIRST_CARD_ORDERED'),
      key: 'FIRST_CARD_ORDERED',
      text: locales.orderCard,
      to: paymentCardRoutes.orderNew,
    },
  ];

  const totalUnskippedItems = ACTION_ITEMS.filter(
    (item) => !item.isSkipped,
  ).length;

  const allActionsCompleted = done?.length === totalUnskippedItems;

  const completedActionCount = ACTION_ITEMS.filter(
    (actionsItem) => actionsItem.isCompleted,
  ).length;

  const progressLabel = `${completedActionCount}/${totalUnskippedItems}`;
  const progressValue = (completedActionCount / totalUnskippedItems) * 100 || 0;

  return (
    <PostOnboardingAccountSetupContext.Provider
      value={{
        actionItems: ACTION_ITEMS,
        allActionsCompleted,
        hasCompletedPostOnboardingSetup: completed,
        loading,
        progressLabel,
        progressValue,
      }}
    >
      {children}
    </PostOnboardingAccountSetupContext.Provider>
  );
};

export const usePostOnboardingAccountSetupContext = () =>
  useContext(PostOnboardingAccountSetupContext);
